import React, { useMemo } from "react";
import { motion } from "framer-motion";

// Lightweight CSS/framer-motion based particle field — no external particle lib required.
export const ParticleField = ({ count = 28 }) => {
    const dots = useMemo(() => {
        const arr = [];
        for (let i = 0; i < count; i++) {
            const size = Math.random() * 2.5 + 0.6;
            arr.push({
                id: i,
                left: Math.random() * 100,
                top: Math.random() * 100,
                size,
                duration: Math.random() * 8 + 6,
                delay: Math.random() * 5,
                opacity: Math.random() * 0.5 + 0.2,
            });
        }
        return arr;
    }, [count]);

    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {dots.map((d) => (
                <motion.span
                    key={d.id}
                    className="particle"
                    style={{
                        left: `${d.left}%`,
                        top: `${d.top}%`,
                        width: `${d.size}px`,
                        height: `${d.size}px`,
                        opacity: d.opacity,
                    }}
                    animate={{
                        y: [-10, 10, -10],
                        x: [-6, 6, -6],
                        opacity: [d.opacity * 0.4, d.opacity, d.opacity * 0.4],
                    }}
                    transition={{
                        duration: d.duration,
                        delay: d.delay,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                />
            ))}
        </div>
    );
};

export const GeometricBackdrop = () => {
    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Grid */}
            <div className="absolute inset-0 grid-pattern radial-fade" />

            {/* Soft glow blobs */}
            <div className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full bg-gold/10 blur-[120px]" />
            <div className="absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full bg-gold/[0.07] blur-[120px]" />

            {/* Geometric SVG accents */}
            <svg
                aria-hidden
                className="absolute top-10 right-10 md:top-24 md:right-24 w-40 h-40 md:w-64 md:h-64 opacity-40 animate-spin-slow"
                viewBox="0 0 200 200"
                fill="none"
            >
                <circle cx="100" cy="100" r="92" stroke="#D4AF37" strokeOpacity="0.25" strokeWidth="1" />
                <circle cx="100" cy="100" r="68" stroke="#D4AF37" strokeOpacity="0.35" strokeWidth="1" />
                <circle cx="100" cy="100" r="44" stroke="#D4AF37" strokeOpacity="0.5" strokeWidth="1" />
                <circle cx="100" cy="100" r="2" fill="#D4AF37" />
            </svg>

            <svg
                aria-hidden
                className="absolute bottom-10 left-6 md:bottom-20 md:left-20 w-36 h-36 md:w-56 md:h-56 opacity-40"
                viewBox="0 0 200 200"
                fill="none"
            >
                <polygon points="100,10 190,180 10,180" stroke="#D4AF37" strokeOpacity="0.3" strokeWidth="1" fill="none" />
                <polygon points="100,40 160,160 40,160" stroke="#D4AF37" strokeOpacity="0.5" strokeWidth="1" fill="none" />
            </svg>

            {/* Subtle scan line */}
            <div className="absolute inset-x-0 top-1/2 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />

            <ParticleField count={32} />
        </div>
    );
};

export default GeometricBackdrop;
